import { Fragment } from 'react';

import { Card } from '@components/card';
import { ExpandableText } from '@components/expandable-text';
import { HideContainer } from '@components/hide-container';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconSend } from '@components/icons/send';
import { InfoBox } from '@components/info-box';
import { useIntegrationWebhook, useWebhookPayloadVariableGroup } from '@components/integrations';
import { findMappedField } from '@components/integrations/condition';
import {
  IntegrationEndpointVariable,
  IntegrationWebhook,
} from '@components/integrations/integrations-types';
import { useListOptions } from '@components/integrations/lists';
import { LabelTextGroup } from '@components/label-text-group';
import { RelativeSpinner } from '@components/loading-spinner';
import { Sidebar } from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { isBase64File } from '@utils/strings';

import { useMarketplaceTemplate } from 'api/app-marketplace/hooks';
import {
  useActivityLogs,
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import {
  AppNotConnectedSection,
  DisabledSection,
  SidebarWarningSection,
} from 'ui/actions/integration/common';
import { useVariableGroups } from 'ui/journey-builder/node-types/common';

import {
  WebhookConditionComponent,
  WebhookConditionData,
  WebhookNotSubscribedSection,
} from './common';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  Base64DownlodableLink,
  BaseContentProps,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  isActionError,
  useActionTabs,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.webhook;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const values = travellerActionQuery.data?.values;
  const {
    data: webhookData,
    isLoading: webhookIsLoading,
    isError: webhookIsError,
  } = useIntegrationWebhook(values?.integrationWebhookId?.value ?? '');

  const { data: template } = useMarketplaceTemplate(webhookData?.templateId ?? '');
  const hasAuthError = Boolean(
    webhookData?.templateId && template?.connectionStatus !== 'connected',
  );
  const hasSubscribeError = Boolean(
    !hasAuthError && webhookData?.isSubscribable && webhookData?.subscribeValid === false,
  );
  const { data: activityLogs } = useActivityLogs(shortId);
  const activityCount = activityLogs?.length ?? 0;
  const action = travellerActionQuery.data;

  const wrapContent = (content: JSX.Element) => {
    const Container = hasAuthError || hasSubscribeError ? DisabledSection : Fragment;
    return webhookIsError ? (
      <InfoBox variant="red" icon={<IconFormIndicatorError />} text={t('webhookError')} />
    ) : (
      <>
        {hasAuthError && (
          <SidebarWarningSection>
            <AppNotConnectedSection template={template} travellerActionId={shortId} />
          </SidebarWarningSection>
        )}
        {hasSubscribeError && (
          <SidebarWarningSection>
            <WebhookNotSubscribedSection webhookShortId={webhookData?.shortId ?? ''} />
          </SidebarWarningSection>
        )}
        <Container>{content}</Container>
      </>
    );
  };

  const { tabs } = useActionTabs({
    actionId: shortId ?? '',
    activityCount,
    display:
      action && webhookData ? (
        wrapContent(
          <DetailsContent
            action={action}
            webhook={webhookData}
            hideFooter={hasAuthError || hasSubscribeError}
          />,
        )
      ) : (
        <></>
      ),
    edit:
      action && webhookData ? (
        wrapContent(
          <EditContent
            action={action}
            webhook={webhookData}
            hideFooter={hasAuthError || hasSubscribeError}
          />,
        )
      ) : (
        <></>
      ),
  });

  if (travellerActionQuery.isLoading || webhookIsLoading) {
    return <RelativeSpinner />;
  }
  if (!shortId || !action) {
    return <div>{t('dataNotFound')}</div>;
  }

  return (
    <Sidebar>
      <TravellerActionHeader
        actionType={ACTION_TYPE}
        title={webhookData?.name || undefined}
        subtitle={webhookData?.integrationName}
        subtitleFont="regular12"
        subtitleLocation="below"
        customIcon={
          webhookData?.thumbnail ? <SquareImage src={webhookData.thumbnail} /> : undefined
        }
        tabs={tabs}
      >
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
    </Sidebar>
  );
};
type ContentProps = BaseContentProps<TravellerJourneyActionDetailed<typeof ACTION_TYPE>> & {
  webhook: IntegrationWebhook;
  hideFooter?: boolean;
};

const DetailsContent = ({ action, webhook, hideFooter }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);

  return (
    <Stack space={6}>
      <ActionProgressBar action={action} isError={isError} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <ExpandableText text={webhook?.description} />
          <LabelTextGroup label={t('description')} text={action.description} />
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <ConditionData action={action} webhook={webhook} />
      {action.status === 'completed' && (
        <>
          <Sidebar.Separator />
          <PayloadDataContent action={action} webhook={webhook} />
        </>
      )}
      {!hideFooter && (
        <TravellerActionFooter
          action={action}
          startIcon={<IconSend />}
          startText={t('run')}
          retryText={t('retry')}
        />
      )}
    </Stack>
  );
};

const EditContent = ({ action, webhook, hideFooter }: ContentProps) => {
  const { stopEditing } = useEditingContext();
  const values = action?.values;
  const visibleConditions = (webhook.conditions ?? []).filter((condition) => condition.visible);
  const conditionDefaultData = values?.conditionsData ?? {};
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const baseVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId ?? '',
    types: ['email', 'text', 'select', 'multi_select', 'phone', 'date', 'separator', 'boolean'],
  });

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSubmit = async (data: WebhookConditionData, description: string) => {
    editMutation.mutateAsync({
      isAutomatic: true,
      action: 'webhook',
      description: description,
      values: {
        ...values,
        conditionsData: data,
      },
    });
  };

  return (
    <WebhookConditionComponent
      integrationId={webhook.shortId ?? ''}
      initialConditions={visibleConditions}
      variableGroups={baseVariableGroups}
      defaultData={conditionDefaultData}
      onCancel={stopEditing}
      onSubmit={handleSubmit}
      fieldMappingId={fieldMappingId}
      description={action.description}
      webhookData={webhook}
      hideFooter={hideFooter}
    />
  );
};

const ConditionData = ({ action, webhook }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const visibleConditions = (webhook.conditions ?? []).filter((condition) => condition.visible);
  const conditionDefaultData = action.data?.conditionsData ?? {};
  const payloadVariableGroups = useWebhookPayloadVariableGroup({ id: webhook.shortId, webhook });
  return (
    <HideContainer title={t('conditions')}>
      <Stack space={4}>
        {visibleConditions.map((condition) => {
          const label =
            (condition.field?.variables && Object.values(condition.field.variables)[0].label) ?? '';
          const value = conditionDefaultData[condition.id] ?? '';
          const listId = findMappedField(condition.field, payloadVariableGroups)?.optionsFromListId;
          return listId ? (
            <ListConditionValue key={condition.id} label={label} listId={listId} value={value} />
          ) : (
            <LabelTextGroup font="regular14" key={condition.id} label={label} text={value} />
          );
        })}
      </Stack>
    </HideContainer>
  );
};

const ListConditionValue = ({
  label,
  listId,
  value,
}: {
  label: string;
  listId: string;
  value: string | string[];
}) => {
  const options = useListOptions(listId);
  const optionLabels = [value].flat().map((optionValue) => {
    const option = options.find((listOption) => listOption.value === optionValue);
    return typeof option?.label === 'string' ? option.label : optionValue;
  });
  return <LabelTextGroup font="regular14" label={label} text={optionLabels} />;
};

const RenderArrayField = ({
  field,
  value,
}: {
  field: IntegrationEndpointVariable;
  value?: { [key: string]: any }[];
}) => {
  if (!value) {
    return <LabelTextGroup font="regular14" key={field.name} label={field.label} />;
  }
  return (
    <Stack space={2}>
      <Text font="semibold14" key={field.name} text={field.label} />
      {(value ?? []).map((item: { [key: string]: any }, idx) => (
        <Card space={4} key={idx}>
          <Stack space={2}>
            {(field.template ?? []).map((template) => {
              const value = item[template.name];
              if (template.type === 'array') {
                return <RenderArrayField key={template.name} field={template} value={value} />;
              }
              return (
                <Fragment key={template.name}>
                  {isBase64File(value) ? (
                    <div>
                      <Text font="semibold14" text={field.label} />
                      <Base64DownlodableLink base64string={value} showAsCard />
                    </div>
                  ) : (
                    <LabelTextGroup
                      font="regular14"
                      key={template.name}
                      label={template.label}
                      text={
                        template.type === 'date' && value
                          ? abbreviateDate(value, 'en')
                          : template.type === 'boolean'
                            ? String(value)
                            : value
                      }
                    />
                  )}
                </Fragment>
              );
            })}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
};

const renderValue = (value: any, fieldType: IntegrationEndpointVariable['type']) => {
  if (value === undefined || value === null) {
    return '';
  }

  switch (fieldType) {
    case 'date':
      return abbreviateDate(value, 'en');
    case 'boolean':
      return String(value);
    default:
      if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
        return JSON.stringify(value);
      }
      return String(value);
  }
};

const PayloadDataContent = ({ action, webhook }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  return (
    <HideContainer title={t('payloadValues')}>
      <Stack space={4}>
        {webhook.payloadVariables.map((field) => {
          const value = action.data.payloadData[field.name];
          if (field.type === 'array') {
            return <RenderArrayField key={field.name} field={field} value={value} />;
          }
          return (
            <Fragment key={field.name}>
              {isBase64File(value) ? (
                <div>
                  <Text font="semibold14" text={field.label} />
                  <Base64DownlodableLink base64string={value} showAsCard />
                </div>
              ) : (
                <LabelTextGroup
                  font="regular14"
                  key={field.name}
                  label={field.label}
                  text={renderValue(value, field.type)}
                />
              )}
            </Fragment>
          );
        })}
      </Stack>
    </HideContainer>
  );
};

const sidebarLocale = {
  en: {
    dataNotFound: 'Data not found',
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    actionStatus: 'Action status',
    condition: 'Condition',
    continueWhen: 'Continue when {conditionType} of the following matches',
    all: 'all',
    conditionInstance: '{element} • {comparator} • {value}',
    actionDetails: 'Action details',
    conditionDescription: 'Continues when specific conditions are met',
    conditions: 'Conditions',
    recivedValue: 'Received value • {value}',
    run: 'Run',
    retry: 'Retry',
    payloadValues: 'Payload values',
    webhookError:
      'Failed to load webhook, if the webhook has been deleted please replace the action in the journey builder',
    description: 'Description',
  },
};
